import { useState } from "react";
import { Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const inp = "w-full h-10 rounded-lg border border-input bg-white px-2.5 text-sm disabled:bg-stone-50 disabled:text-stone-500";
export const d = (c) => (c == null ? "" : (c / 100).toFixed(2));
export const LOW = ["review", "unreadable"];

export const STATUS = {
  draft: ["Draft", "bg-stone-100 text-stone-700 border-stone-200"],
  processing: ["Processing", "bg-sky-50 text-sky-800 border-sky-200"],
  needs_review: ["Needs Review", "bg-amber-50 text-amber-900 border-amber-200"],
  approved: ["Approved", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  rejected: ["Rejected", "bg-rose-50 text-rose-800 border-rose-200"],
  failed: ["Failed", "bg-rose-100 text-rose-900 border-rose-300"],
  duplicate: ["Duplicate", "bg-orange-50 text-orange-800 border-orange-300"],
  archived: ["Archived", "bg-stone-50 text-stone-500 border-stone-200"],
};

export function InvStatusBadge({ s, testid }) {
  const [l, c] = STATUS[s] || [s, "bg-stone-100"];
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${c}`} data-testid={testid}>{s === "processing" && <Loader2 className="h-3 w-3 animate-spin" />}{l}</span>;
}

const CONF = { high: ["High confidence", "text-emerald-700"], review: ["Check this", "text-amber-700"], unreadable: ["Couldn't read", "text-rose-700"] };
export const ConfTag = ({ c }) => (CONF[c] ? <span className={`text-[10px] font-semibold uppercase tracking-wide ${CONF[c][1]}`}>{CONF[c][0]}</span> : null);

const STEPS = ["Upload", "Scan", "Review", "Approve", "Confirm"];
export function Stepper({ inv }) {
  const s = inv.display_status;
  const pendingCost = (inv.cost_changes || []).some((c) => ["pending", "later"].includes(c.decision));
  const at = s === "processing" ? 1 : s === "approved" ? (pendingCost ? 4 : 5) : ["rejected", "archived"].includes(s) ? -1 : inv.readiness?.length ? 2 : 3;
  if (at < 0) return null;
  return (
    <ol className="flex items-center gap-1 sm:gap-2 text-[11px] sm:text-xs overflow-x-auto no-scrollbar py-1" data-testid="invoice-stepper">
      {STEPS.map((l, i) => (
        <li key={l} className="flex items-center gap-1 sm:gap-2 shrink-0">
          <span className={`h-6 w-6 rounded-full flex items-center justify-center font-semibold ${i < at ? "bg-stone-900 text-white" : i === at ? "ring-2 ring-stone-900 bg-white" : "bg-stone-100 text-stone-400"}`}>{i < at ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
          <span className={i === at ? "font-semibold text-stone-900" : "text-stone-500"}>{l}</span>
          {i < STEPS.length - 1 && <span className="w-3 sm:w-6 h-px bg-stone-300" />}
        </li>))}
    </ol>
  );
}

export function ReasonDialog({ open, onClose, title, text, confirmLabel, danger, needReason = true, onConfirm, testid }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(reason.trim()); setReason(""); onClose(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{text}</DialogDescription></DialogHeader>
        {needReason && <textarea className="w-full min-h-[80px] rounded-lg border border-input p-2 text-sm" placeholder="Reason (required, saved to the audit trail)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} />}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className={danger ? "bg-rose-700 hover:bg-rose-800" : ""} disabled={busy || (needReason && !reason.trim())} onClick={go} data-testid={`${testid}-confirm`}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
